import { useEffect, useState } from 'react';

import type { IUseImage } from './image';

interface IMediaURL {
	mediaUrl: {
		type: 'doc' | 'image' | null;
		url: string;
		load: boolean;
	};
}

const isImageExist = async (url: string, imgProps: IUseImage): Promise<boolean> =>
	await new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			imgProps.handleLoad(true);
			resolve(true);
			cleanup();
		};
		img.onerror = () => {
			imgProps.handleError(true);
			resolve(false);
			cleanup();
		};
		img.src = url;

		// Cleanup function to remove event listeners and clear the src attribute
		const cleanup = () => {
			img.onload = null;
			img.onerror = null;
			img.src = '';
			imgProps.handleLoad(false);
			imgProps.handleError(false);
		};
	});

export const useGetMediaUrl = (url: string, imgProps: IUseImage): IMediaURL => {
	const [mediaUrl, setMediaUrl] = useState<{
		type: 'doc' | 'image' | null;
		url: string;
		load: boolean;
	}>({ type: 'image', url: '', load: false });

	useEffect(() => {
		setMediaUrl({ load: true, type: null, url: '' });
		const getMediaUrl = async (): Promise<{
			type: 'doc' | 'image' | null;
			url: string;
			load: boolean;
		}> => {
			if (url) {
				const isImage = await isImageExist(url, imgProps);
				return {
					type: isImage ? 'image' : 'doc',
					url,
					load: false,
				};
			}
			return { type: 'image', url: '', load: false };
		};
		getMediaUrl().then((data) => {
			setMediaUrl(data);
		});
	}, [url]);

	return {
		mediaUrl,
	};
};
